import { useState } from "react";
import { Sparkles } from "lucide-react";
import type { ActionButton, CustomFieldProposal } from "./types";

// foundation.customization Phase 4C — the confirm card rendered for a
// `propose_custom_field` ActionButton. Summarises the proposed field +
// placement + scope; on confirm it calls onConfirm(proposal), which drives
// the two ede.create commands + view re-compose (owned by AssistantPanel).
// Nothing is written until the user clicks Add field.

interface Props {
    action: ActionButton;
    disabled?: boolean;
    onConfirm: (proposal: CustomFieldProposal) => Promise<void>;
}

export function CustomFieldConfirmCard({ action, disabled, onConfirm }: Props) {
    const proposal = action.payload as unknown as CustomFieldProposal;
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const f = proposal.field;

    const handleConfirm = async () => {
        setBusy(true);
        try {
            await onConfirm(proposal);
            setDone(true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="assistant-confirm-card" data-testid="custom-field-confirm-card">
            <div className="assistant-confirm-card__head">
                <Sparkles size={13} aria-hidden="true" />
                <span>{action.label}</span>
            </div>
            <dl className="assistant-confirm-card__rows">
                <dt>Field</dt>
                <dd>
                    {f.label} ({f.type})
                </dd>
                {f.type === "selection" && f.options ? (
                    <>
                        <dt>Options</dt>
                        <dd>{f.options.map((o) => o.label).join(", ")}</dd>
                    </>
                ) : null}
                <dt>Placement</dt>
                <dd>
                    {proposal.placement.position} {proposal.placement.xpath}
                </dd>
                <dt>Scope</dt>
                <dd>{proposal.scope}</dd>
            </dl>
            <div className="assistant-confirm-card__foot">
                {done ? (
                    <span className="assistant-confirm-card__done" data-testid="custom-field-confirm-done">
                        Added.
                    </span>
                ) : (
                    <button
                        type="button"
                        className="assistant-confirm-card__add"
                        data-testid="custom-field-confirm-btn"
                        disabled={disabled || busy}
                        onClick={handleConfirm}
                    >
                        {busy ? "Adding…" : "Add field"}
                    </button>
                )}
            </div>
        </div>
    );
}
